/**
 * Paleta sóbria do quadro kanban e do modal da OS.
 *
 * `corStatus()` do `@tcm/shared` continua sendo a cor oficial do sistema — é
 * ela que pinta as bolinhas da parede, os badges e os relatórios, e mexer nela
 * mudaria oito telas de uma vez. O que ela não faz é conviver com o creme: o
 * vermelho #dc2626 e o azul #3b82f6 são cores de sinalização, saturadas demais
 * ao lado do âmbar da marca.
 *
 * Aqui a MESMA semântica (a categoria do status, nunca `includes` no texto)
 * ganha um tom terroso da mesma família do creme: terracota, azul-petróleo,
 * oliva, mostarda e pedra. O status continua legível de longe pela posição na
 * escala quente→fria, e o laranja-âmbar fica reservado para o que é ação do
 * operador — botão, foco, alvo de soltar, OS parada há tempo demais.
 *
 * Três valores por tom, porque é o que a interface pede em todo lugar:
 *  • `forte` — filete, bolinha, texto sobre creme;
 *  • `suave` — fundo de etiqueta;
 *  • `texto` — texto sobre `suave`, já com contraste conferido.
 */
import { acharStatus, type CategoriaStatus } from '@tcm/shared';

export interface TomStatus {
  forte: string;
  suave: string;
  texto: string;
}

const TONS: Record<CategoriaStatus, TomStatus> = {
  /** Terracota — parado esperando alguém. */
  aguardando: { forte: '#b5502f', suave: '#f7e9e2', texto: '#8c3d23' },
  /** Azul-petróleo — a única cor fria, para o que está andando. */
  andamento: { forte: '#4f6e85', suave: '#e6ecf1', texto: '#3d566a' },
  /** Oliva — concluído, sem o verde-semáforo. */
  concluido: { forte: '#5f7a52', suave: '#e9eee4', texto: '#4a6140' },
  /** Mostarda — pendência, vizinha do âmbar da marca. */
  pendente: { forte: '#a87c2a', suave: '#f6ecd9', texto: '#856118' },
  /** Pedra — encerrado sem desfecho, some do fundo de propósito. */
  cancelado: { forte: '#8c8378', suave: '#efebe4', texto: '#6b645b' },
};

export function tomStatus(codigo: string | null | undefined): TomStatus {
  return TONS[acharStatus(codigo).categoria];
}

/** Âmbar da marca, para o que é ação ou alerta — não é status. */
export const AMBAR = {
  forte: '#d97706',
  suave: '#fdf3e2',
  texto: '#b45309',
} as const;
